import React, { ReactElement, useEffect, useState } from "react";
import {
    Nav, Navbar, NavDropdown,
} from "react-bootstrap";
import { AiOutlineGift } from "react-icons/ai";
import { BsGearFill, BsPlusSquare } from "react-icons/bs";
import { IoPerson } from "react-icons/io5";
import {
    MdHelpOutline, MdOutlineMarkChatUnread, MdOutlineTipsAndUpdates,
} from "react-icons/md";
import logo from "../../assets/img/logo-white.svg";
import useCreateTicket from "../../contexts/CreateTicketContext";
import usePortal from "../../contexts/PortalContext";
import useEvents from "../../contexts/PortalSubscriptionContext";
import useRouting, { isRouteMatch, NavLink } from "../../contexts/Routing";
import useUpdatesContext from "../../contexts/updates/UpdatesContext";
import useCurrentSession from "../../contexts/UserContext";
import { Permission, PortalEventType } from "../../__generated__/api";
import Avatar from "../avatar/Avatar";
import CodeBlock from "../code-block/CodeBlock";
import Confirm from "../confirm/Confirm";
import "./Navigation.css";

/** Navigation Bar */

// eslint-disable-next-line max-len

export default function Navigation(): ReactElement {
    const {
        isLoggedIn, user, logout, current: currentSession, clearCache,
    } = useCurrentSession();
    const {
        permissions, featureFlags, id: portalId, invalidate: invalidatePortal,
    } = usePortal();

    const updates = useUpdatesContext();

    const { createHistoryPush, Paths, historyPush } = useRouting();

    const isRouteAgent = isRouteMatch({ path: Paths.AGENT, exact: false, strict: false });
    const isRouteTickets = isRouteMatch({ path: Paths.TICKETS, exact: false, strict: false });

    const [hasMessage, setHasMessages] = useState(true);
    const createTicket = useCreateTicket();

    const [showDebug, setShowDebug] = useState(false);

    useEffect(() => {
        if (hasMessage && (isRouteAgent || isRouteTickets)) {
            setHasMessages(false);
        }
    }, [isRouteAgent, isRouteTickets, hasMessage]);

    useEvents(api => {
        api.on(PortalEventType.MessageNew, 0, () => {
            setHasMessages(true);
        });
        api.on(PortalEventType.ConversationNew, 0, () => {
            setHasMessages(true);
        });
    });

    return (
        <div>
            {showDebug && (
                <Confirm shouldShow onHide={() => setShowDebug(false)}>
                    <div>
                        <h5>Debugging Info</h5>
                        <small>
                            This information is useful when talking to support.
                        </small>

                        <div>
                            <button
                                onClick={() => {
                                    clearCache();
                                    invalidatePortal();
                                }}
                                type="button"
                                className="btn btn-none"
                            >
                                Clear Cache
                            </button>
                        </div>

                        <div className="mb-2">
                            <small className="text-light">Please send the following information to support</small>
                            <div>
                                <CodeBlock
                                    code={
                                        Object.entries({
                                            "User Id": user?.id,
                                            "Session Id": currentSession?.id,
                                            "Portal Id": portalId,
                                            Group: user?.group,
                                            Role: user?.role,
                                            "User Groups": user?.userGroups?.toIdList("id"),
                                            Permissions: Array.from(permissions),
                                            "Feature Flags": Array.from(featureFlags),
                                        })
                                            .filter(([, value]) => value != null)
                                            .map(([key, value]) => {
                                                const formatted = Array.isArray(value) ? `\n\t${value.join(",\n\t")}` : value;
                                                return `${key}: ${formatted}`;
                                            })
                                            .join("\n")
                                    }
                                    isCopyable
                                    size="sm"
                                    alert="light"
                                />
                            </div>
                        </div>

                    </div>
                </Confirm>
            )}
            <Navbar className="nav ps-3 bg-gradient p-0" expand="lg">
                <Navbar.Brand
                    href="/"
                    className="m-0 p-0 me-2 my-2"
                    onClick={evt => {
                        evt.preventDefault();
                        historyPush(Paths.HOME);
                    }}
                >
                    <img src={logo} alt="Foqal logo" className="nav-logo" />
                </Navbar.Brand>
                <Navbar.Toggle aria-controls="responsive-navbar-nav" />
                <Navbar.Collapse id="responsive-navbar-nav" className="w-100">
                    <Nav className="nav-responsive align-items-center p-0 d-flex w-100 justify-content-between">
                        <div className="d-flex align-items-top">
                            {permissions.has(Permission.ArticleReadPublic) && (
                                <NavLink
                                    to={Paths.ANSWERS_LIST}
                                    className="navbar-link mx-lg-0 mx-2 text-gray-300"
                                >
                                    Answers
                                </NavLink>
                            )}

                            {permissions.has(Permission.ChatsModify) && (
                                <NavLink
                                    to={Paths.AGENT}
                                    className="navbar-link mx-lg-2 mx-2 px-1 text-gray-300 position-relative d-block"
                                >
                                    Tickets
                                    {hasMessage && (
                                        <span
                                            className={"position-absolute top-0 start-100 translate-middle "
                                                + "bg-danger badge p-1 rounded-circle"}
                                        >
                                            <MdOutlineMarkChatUnread />
                                            <span className="visually-hidden">New alerts</span>
                                        </span>
                                    )}

                                </NavLink>
                            )}

                            {permissions.has(Permission.UsersCreate) && (

                                <NavLink
                                    to={Paths.CONTACTS}
                                    className="navbar-link mx-lg-0 mx-2 text-gray-300"
                                >
                                    Contacts
                                </NavLink>
                            )}

                            {!permissions.has(Permission.ChatsModify)
                                && (permissions.has(Permission.TicketsReadSelf) || permissions.has(Permission.TicketsRead))
                                && (
                                    <NavLink
                                        to={Paths.TICKETS}
                                        className="navbar-link text-gray-300 position-relative"
                                    >
                                        My Tickets
                                        {hasMessage && (
                                            <span
                                                className={"position-absolute top-0 start-100 translate-middle "
                                                    + "bg-danger badge p-1 rounded-circle"}
                                            >
                                                <MdOutlineMarkChatUnread />
                                                <span className="visually-hidden">New tickets</span>
                                            </span>
                                        )}
                                    </NavLink>
                                )}

                        </div>

                        <div className="d-flex align-items-center flex-column flex-lg-row">

                            {(permissions.has(Permission.ChatsModifyAll)) && (
                                <button
                                    type="button"
                                    onClick={() => updates.show()}
                                    className="navbar-link p-0 mx-3 btn btn-none text-light position-relative"
                                >

                                    <AiOutlineGift className="fs-5" />
                                    <span className="d-lg-none ms-2">Updates</span>
                                    {updates.unreadCount() > 0 && (
                                        <span
                                            className={"position-absolute top-0 start-100 translate-middle "
                                                + "bg-button-hover badge p-1 rounded-circle"}
                                        >
                                            <MdOutlineTipsAndUpdates />
                                            <span className="visually-hidden">New Updates</span>
                                        </span>
                                    )}
                                </button>
                            )}
                            {(permissions.has(Permission.ArticleModify)
                                || permissions.has(Permission.TicketsModify)
                                || permissions.has(Permission.TicketCreatePublic)
                                || permissions.has(Permission.TicketCreateGuest)
                            )
                                && (
                                    <NavDropdown
                                        data-test="create-dropdown"
                                        align="end"
                                        title={(
                                            <span>
                                                <BsPlusSquare className="fs-5" />
                                                <span className="d-lg-none ms-2">Create</span>
                                            </span>
                                        )}
                                        id="collapsible-nav-dropdown"
                                        bsPrefix="navbar-link"
                                        className="navdropdown-link m-lg-0 m-2"
                                    >
                                        {permissions.has(Permission.ArticleModify) && (
                                            <NavDropdown.Item
                                                eventKey="3"
                                                data-test="new-answer"
                                                onClick={createHistoryPush(Paths.ANSWERS_NEW)}
                                            >
                                                Article
                                            </NavDropdown.Item>
                                        )}
                                        {permissions.has(Permission.TicketsModify) && (
                                            <NavDropdown.Item
                                                eventKey="4"
                                                data-test="agent-compose"
                                                onClick={createHistoryPush(Paths.AGENT_COMPOSE)}
                                            >
                                                Compose Message
                                            </NavDropdown.Item>
                                        )}
                                        {(
                                            permissions.has(Permission.TicketCreatePublic)
                                            || permissions.has(Permission.TicketCreateGuest)
                                            || permissions.has(Permission.ChatsModify)
                                        )
                                            && (
                                                <NavDropdown.Item
                                                    eventKey="5"
                                                    data-test="ticket-create"
                                                    onClick={() => createTicket.show()}
                                                >
                                                    Request
                                                </NavDropdown.Item>
                                            )}

                                    </NavDropdown>

                                )}
                            {(permissions.has(Permission.Administrator)) && (
                                <NavLink
                                    to={Paths.ADMINISTRATOR}
                                    className="navbar-link mx-lg-0 mx-5"
                                >
                                    <BsGearFill className="fs-5" />
                                    <span className="d-lg-none ms-2">Settings</span>
                                </NavLink>
                            )}
                            {(!isLoggedIn || !user) && (
                                <NavLink
                                    to={Paths.LOGIN}
                                    className="navbar-link"
                                >
                                    Sign In
                                </NavLink>
                            )}

                            {(isLoggedIn && user) && (
                                <NavDropdown
                                    data-test="user-account"
                                    align="end"
                                    title={(
                                        <>
                                            <Avatar
                                                src=""
                                                variant="sm"
                                                defaultIcon={
                                                    <IoPerson className="me-2" />
                                                }
                                            />
                                            <span className="d-lg-none">Profile</span>
                                        </>
                                    )}
                                    id="collapsible-nav-dropdown"
                                    bsPrefix="navbar-link"
                                    className="navdropdown-link"
                                >
                                    <NavDropdown.Header className="d-flex flex-column align-items-end">
                                        <div className="fs-6">
                                            Welcome
                                            {" "}
                                            {user.name}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowDebug(true)}
                                            className="btn btn-none text-light fs-6 p-0"
                                        >
                                            <small>
                                                Support Info
                                            </small>
                                        </button>
                                    </NavDropdown.Header>

                                    <NavDropdown.Divider className="mx-3 mb-4 mt-0" />

                                    {permissions.has(Permission.SuperAdmin) && (
                                        <NavLink
                                            to={Paths.SITE_ADMINISTRATOR}
                                            className="text-end dropdown-item"
                                        >
                                            Site Admin
                                        </NavLink>
                                    )}

                                    {permissions.has(Permission.ChatsModifyAll) && (
                                        <NavLink
                                            to={Paths.ACCOUNT_PROFLE}
                                            className="text-end dropdown-item"
                                        >
                                            My Profile
                                        </NavLink>
                                    )}

                                    {permissions.has(Permission.ChatsModify) && (
                                        <NavLink
                                            to={Paths.TICKETS}
                                            className="text-end dropdown-item"
                                        >
                                            My Tickets
                                        </NavLink>
                                    )}

                                    {/* <NavDropdown.Item
                                        data-test="profile"
                                        eventKey="1"
                                        onClick={createHistoryPush(Paths.ACCOUNT_PROFLE)}
                                        className="text-end"
                                    >
                                        Profile
                                    </NavDropdown.Item> */}

                                    <NavDropdown.Item
                                        onClick={logout}
                                        className="text-end"
                                    >
                                        <div data-test="logout">
                                            Logout
                                        </div>
                                    </NavDropdown.Item>
                                </NavDropdown>
                            )}
                        </div>
                    </Nav>
                </Navbar.Collapse>
            </Navbar>
        </div>
    );
}
